import React, { useRef } from 'react';
import { cn } from '../../lib/cn';

export interface SegmentedOption<T extends string | number = string> {
  value: T;
  label: React.ReactNode;
  icon?: React.ReactNode;
  /** Подсказка (title) */
  hint?: string;
  disabled?: boolean;
}

export interface SegmentedControlProps<T extends string | number = string> {
  options: SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
  size?: 'sm' | 'md';
  fullWidth?: boolean;
  'aria-label'?: string;
  className?: string;
}

/** Переключатель 2–6 вариантов (radiogroup со стрелками клавиатуры). */
export function SegmentedControl<T extends string | number = string>({
  options,
  value,
  onChange,
  size = 'md',
  fullWidth,
  className,
  ...rest
}: SegmentedControlProps<T>) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);

  const move = (from: number, dir: 1 | -1) => {
    const n = options.length;
    for (let k = 1; k <= n; k++) {
      const i = (((from + dir * k) % n) + n) % n;
      if (!options[i].disabled) {
        onChange(options[i].value);
        refs.current[i]?.focus();
        return;
      }
    }
  };

  return (
    <div
      role="radiogroup"
      aria-label={rest['aria-label']}
      className={cn(
        'inline-flex rounded-md border border-gray-300 bg-gray-50 p-0.5 dark:border-zinc-700 dark:bg-zinc-800/60',
        fullWidth && 'flex w-full',
        className,
      )}
    >
      {options.map((o, i) => {
        const selected = o.value === value;
        return (
          <button
            key={String(o.value)}
            ref={(el) => {
              refs.current[i] = el;
            }}
            type="button"
            role="radio"
            aria-checked={selected}
            title={o.hint}
            disabled={o.disabled}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(o.value)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
                e.preventDefault();
                move(i, 1);
              } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
                e.preventDefault();
                move(i, -1);
              }
            }}
            className={cn(
              'inline-flex items-center justify-center gap-1.5 rounded transition-colors disabled:cursor-not-allowed disabled:opacity-40',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
              size === 'sm' ? 'px-2.5 py-1 text-xs' : 'px-3 py-1.5 text-sm',
              fullWidth && 'flex-1',
              selected
                ? 'bg-white font-medium text-indigo-700 shadow-sm dark:bg-zinc-900 dark:text-indigo-300'
                : 'text-gray-600 hover:text-gray-900 dark:text-zinc-400 dark:hover:text-zinc-100',
            )}
          >
            {o.icon && <span className="shrink-0">{o.icon}</span>}
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export default SegmentedControl;
